export interface GuestSession {
  totalScore: number;
  gamesPlayed: number;
  bestGameScore: number;
  currentStreak: number;
  lastPlayed: string;
  sessionStarted: string;
  perfectScores: number;
}

const GUEST_SESSION_KEY = 'speechcoach_guest_session';
const SESSION_EXPIRY_DAYS = 30;

class GuestSessionService {
  private session: GuestSession | null = null;

  constructor() {
    this.loadSession();
  }

  private loadSession(): void {
    try {
      const stored = localStorage.getItem(GUEST_SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored) as GuestSession;
        // Check if session is expired (30 days)
        const lastPlayed = new Date(session.lastPlayed);
        const daysSinceLastPlayed = (Date.now() - lastPlayed.getTime()) / (1000 * 60 * 60 * 24);

        if (daysSinceLastPlayed < SESSION_EXPIRY_DAYS) {
          this.session = session;
        } else {
          // Session expired, create new one
          this.createNewSession();
        }
      } else {
        this.createNewSession();
      }
    } catch (error) {
      console.error('Error loading guest session:', error);
      this.createNewSession();
    }
  }

  private createNewSession(): void {
    this.session = {
      totalScore: 0,
      gamesPlayed: 0,
      bestGameScore: 0,
      currentStreak: 0,
      lastPlayed: new Date().toISOString(),
      sessionStarted: new Date().toISOString(),
      perfectScores: 0
    };
    this.saveSession();
  }

  private saveSession(): void {
    if (this.session) {
      try {
        localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(this.session));
      } catch (error) {
        console.error('Error saving guest session:', error);
      }
    }
  }

  public addGameResult(score: number, perfectScores: number = 0): void {
    if (!this.session) {
      this.createNewSession();
    }

    this.session!.totalScore += score;
    this.session!.gamesPlayed += 1;
    this.session!.perfectScores += perfectScores;

    if (score > this.session!.bestGameScore) {
      this.session!.bestGameScore = score;
    }

    // Update streak (if playing daily)
    const lastPlayed = new Date(this.session!.lastPlayed);
    const today = new Date();
    const daysSinceLastPlayed = Math.floor((today.getTime() - lastPlayed.getTime()) / (1000 * 60 * 60 * 24));

    if (daysSinceLastPlayed === 0) {
      // Playing same day, streak continues
    } else if (daysSinceLastPlayed === 1) {
      // Playing next day, increment streak
      this.session!.currentStreak += 1;
    } else {
      // Missed days, reset streak
      this.session!.currentStreak = 1;
    }

    this.session!.lastPlayed = new Date().toISOString();
    this.saveSession();
  }

  public getSession(): GuestSession | null {
    return this.session;
  }

  public getTotalScore(): number {
    return this.session?.totalScore || 0;
  }

  public getGamesPlayed(): number {
    return this.session?.gamesPlayed || 0;
  }

  public shouldPromptLogin(): { shouldPrompt: boolean; reason?: string } {
    if (!this.session) return { shouldPrompt: false };

    const { totalScore, gamesPlayed, bestGameScore } = this.session;

    // Prompt after first game
    if (gamesPlayed === 1) {
      return { shouldPrompt: true, reason: 'first_game' };
    }

    // Prompt at score milestones
    if (totalScore >= 500 && totalScore < 550) {
      return { shouldPrompt: true, reason: 'milestone_500' };
    }

    if (totalScore >= 1000 && totalScore < 1050) {
      return { shouldPrompt: true, reason: 'milestone_1000' };
    }

    // Prompt after exceptional game
    if (bestGameScore >= 100) {
      return { shouldPrompt: true, reason: 'great_game' };
    }

    // Prompt every 5 games
    if (gamesPlayed % 5 === 0 && gamesPlayed > 0) {
      return { shouldPrompt: true, reason: 'games_milestone' };
    }

    return { shouldPrompt: false };
  }

  public clearSession(): void {
    localStorage.removeItem(GUEST_SESSION_KEY);
    this.session = null;
  }

  public transferToUser(userId: string): void {
    // This would be called when user signs in to transfer their guest scores
    // The actual transfer would happen on the backend
    console.log(`Transferring guest session to user ${userId}:`, this.session);
    // After successful transfer, clear the guest session
    this.clearSession();
  }
}

export default new GuestSessionService();